import React, { useCallback } from "react";

import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons/faEllipsisH";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import DownloadMutipleEntitiesModal from "@app/component/DownloadMultipleEntitiesModal";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import DownloadEntitiesMapping from "@app/types/DownloadEntitiesMapping";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";
import useAnalytics from "@app/util/contexts/useAnalytics";

import CustomizeMapSortModal from "../CustomizeMapSortModal";

import styles from "./CustomizeMenu.module.scss";

export interface CustomizeMenuProps {
  analyticsKeyPath?: string | string[];
  onChangeSort?: (sort: SortModel, query?: AreaSearchQuery) => void;
  onDownloadRecords?: (entityColumnsMap: DownloadEntitiesMapping) => void;
  sort: SortModel;
  sortQuery?: AreaSearchQuery;
  visibleEntities?: EntityTypesSupportedByMapsPage[];
}

const CustomizeMenu: React.FC<CustomizeMenuProps> = ({
  analyticsKeyPath = "Records List",
  onChangeSort,
  onDownloadRecords,
  sort,
  sortQuery,
  visibleEntities,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [visible, show, hide] = useBoolean(false);
  const [downloadModal, showDownloadModal, hideDownloadModal] = useBoolean();
  const handleDownloadRecords = useCallback(
    (entityColumnsMap: DownloadEntitiesMapping) => {
      analytics.clicked(["Records in View", "Export"]);
      onDownloadRecords?.(entityColumnsMap);
    },
    [analytics, onDownloadRecords]
  );

  const handleOpenChange = useCallback(
    (open) => {
      if (open) {
        show();
      } else {
        hide();
      }
    },
    [hide, show]
  );

  const handleShowCustomize = useCallback(() => {
    window.open(`${Path.SETTINGS}/${SettingPath.PINNED_FIELDS}`, "_blank");
    hide();
  }, [hide]);

  return (
    <ExtendedAnalytics keyPath={analyticsKeyPath}>
      <Dropdown
        onOpenChange={handleOpenChange}
        open={visible}
        overlay={
          visible ? (
            <Menu
              className={styles.menu}
              expandIcon={<span />}
              mode="vertical"
              triggerSubMenuAction="click"
            >
              <Menu.SubMenu
                key="sort"
                popupClassName={styles.submenu}
                title={intl.formatMessage({
                  id: "recordList.customization.menu.sort",
                  defaultMessage: "Sort",
                  description: "View Menu - Sort menu item title",
                })}
              >
                <CustomizeMapSortModal
                  onChange={onChangeSort}
                  onHide={hide}
                  sort={sort}
                  sortQuery={sortQuery}
                />
              </Menu.SubMenu>

              <Menu.Item key="customize" onClick={handleShowCustomize}>
                {intl.formatMessage({
                  id: "recordList.customization.menu.pinnedFields",
                  defaultMessage: "Pinned Fields",
                  description: "View Menu - Pinned Fields menu item title",
                })}
              </Menu.Item>

              <Menu.Item disabled={!onDownloadRecords} key="export" onClick={showDownloadModal}>
                {intl.formatMessage({
                  id: "recordList.customization.menu.download",
                  defaultMessage: "Download",
                  description: "View Menu - Quick Export menu item title",
                })}
              </Menu.Item>
            </Menu>
          ) : (
            <span />
          )
        }
        placement="bottomRight"
        trigger={["click"]}
      >
        <Tooltip
          title={intl.formatMessage({
            id: "recordList.customization.menu.open.tooltip",
            defaultMessage: "Open menu",
            description: "Open Menu toolbar button text",
          })}
        >
          <Button icon={<FontAwesomeIcon icon={faEllipsisH} size="lg" />} type="control" />
        </Tooltip>
      </Dropdown>
      {downloadModal && visibleEntities && (
        <DownloadMutipleEntitiesModal
          hasSorting
          onDownload={handleDownloadRecords}
          onHide={hideDownloadModal}
          visibleEntities={visibleEntities}
        />
      )}
    </ExtendedAnalytics>
  );
};

export default CustomizeMenu;
